import { hotelIdsOf } from "@/lib/hotel-count";
import { resolveCalendarDateRange } from "@/lib/calendar-date-range";
import {
  isFixedDateVoucher,
  isFixedDateVoucherUnavailable,
  resolveDealDateFields,
} from "@/lib/fixed-date-voucher.mjs";
import { resolveDefaultCommissionFallback } from "@/lib/pricing/default-deal-markup";
import { readPositiveCalendarPrice } from "@/lib/booking/non-negative-price";
import { isTegelKlikbaar } from "@/lib/booking/kalender-snelkeuze";
import {
  LINK_DATE_PARAM,
  LINK_LOS_PARAM,
  geldigeLinkDatum,
  geldigeLinkLos,
} from "@/lib/booking/link-voorselectie";
import { localeToBcp47 } from "@/lib/locale-bcp47";
import { kerstHotelRijen } from "@/lib/kerst-feiten";
import { orderHotels } from "@/components/deal/orderHotels";

// Dealpagina (30-10-2026): de voordeligste aankomstdagen onder «Kies datum en
// boek». Zelfde kalender en zelfde regel als de datumstap (DateSelection):
// `POST /api/booking/calendar` met de basisaanvraag van 2 volwassenen, 0
// kinderen (buildCalendarBody(2, 0)), de eerste duur uit `length_of_stay`, en
// dezelfde poort per dag (isDateAvailable + isTegelKlikbaar). Nooit een
// geschatte dag: geen terugval op `snapped_prices`, alleen echte kalenderprijzen.

/** Wacht hoogstens zo lang op de kalender; daarna geen datumregel. */
export const KALENDER_TIMEOUT_MS = 4000;

/** Zoveel dagen noemt de regel hoogstens. */
export const MAX_AANKOMSTDAGEN = 3;

/**
 * De velden van de deal die de kalenderaanvraag en de dagpoort lezen. Een
 * projectie, zodat de servercomponent niet de hele deal in de RSC-payload zet.
 */
export function kalenderDeal(deal) {
  if (!deal || typeof deal !== "object") return null;
  return {
    deals_id: deal.deals_id,
    dealId: deal.dealId,
    hotel_id: deal.hotel_id,
    hotelId: deal.hotelId,
    travel_start_date: deal.travel_start_date,
    travelStartDate: deal.travelStartDate,
    travel_end_date: deal.travel_end_date,
    travelEndDate: deal.travelEndDate,
    booking_end_date: deal.booking_end_date,
    bookingEndDate: deal.bookingEndDate,
    length_of_stay: deal.length_of_stay,
    board_type: deal.board_type,
    snapped_prices: deal.snapped_prices,
    margin_commission: deal.margin_commission,
    marginCommission: deal.marginCommission,
    commission_type: deal.commission_type,
    commissionType: deal.commissionType,
    discount_percentage: deal.discount_percentage,
    discountPercentage: deal.discountPercentage,
    minimum_days_before_checkin: deal.minimum_days_before_checkin,
    minimumDaysBeforeCheckin: deal.minimumDaysBeforeCheckin,
  };
}

// Zelfde lezing als losOptions in DateSelection.
function losOpties(deal) {
  const raw = deal?.length_of_stay;
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

// Zelfde lezing als boekingstermijnVanDeal in DateSelection; zonder eigen
// termijn 0 (BOEKINGSTERMIJN_ONBEKEND): de kalender past de echte termijn al toe.
function termijnVanDeal(deal) {
  const ruw = deal?.minimum_days_before_checkin ?? deal?.minimumDaysBeforeCheckin;
  return typeof ruw === "number" && Number.isFinite(ruw) ? ruw : 0;
}

/**
 * De kalenderaanvraag van de datumstap voor het standaardgezelschap (2+0) en
 * de standaardduur (eerste `length_of_stay`), of null als de deal er geen
 * heeft (geen id, geen duur, geen reisperiode, of een gesloten vaste datum).
 *
 * @returns {{ body: object, los: number, losKey: string, range: {startDate: string, endDate: string}, fixed: boolean, termijn: number } | null}
 */
export function kalenderAanvraag(deal, { now = new Date() } = {}) {
  if (!deal) return null;
  const dealId = Number(deal.dealId || deal.deals_id);
  if (!Number.isInteger(dealId) || dealId <= 0) return null;
  const losOptions = losOpties(deal);
  const los = geldigeLinkLos(losOptions[0]);
  if (los == null) return null;
  if (isFixedDateVoucherUnavailable(deal, now)) return null;
  const fixed = isFixedDateVoucher(deal);
  const { travelStartDate, travelEndDate } = resolveDealDateFields(deal);
  const range = resolveCalendarDateRange(travelStartDate, travelEndDate, {
    now,
    windowMode: fixed ? "fixed-arrival" : "flexible",
  });
  if (!range.startDate || !range.endDate) return null;
  const hotelIds = hotelIdsOf(deal);
  // Veld voor veld gelijk aan buildCalendarBody(2, 0) in DateSelection.
  const body = {
    deal_id: dealId,
    hotel_ids: hotelIds,
    start_date: range.startDate,
    end_date: range.endDate,
    fixed_arrival: fixed,
    length_of_stay: losOptions,
    adults: 2,
    children: 0,
    board_type: deal.board_type || [],
    snapped_prices: deal.snapped_prices || [],
    dealsConfig: {
      deals_id: dealId,
      travel_start_date: range.startDate,
      travel_end_date: range.endDate,
      fixed_arrival: fixed,
      hotel_id: hotelIds,
      length_of_stay: losOptions,
      board_type: deal.board_type || [],
      margin_commission:
        deal.margin_commission ||
        deal.marginCommission ||
        resolveDefaultCommissionFallback(deal.commission_type || deal.commissionType),
      commission_type: deal.commission_type || deal.commissionType || "percentage",
      discount_percentage: deal.discount_percentage || deal.discountPercentage || 0,
      snapped_prices: deal.snapped_prices || [],
      travel_companion: 2,
    },
  };
  return {
    body,
    los,
    losKey: "0",
    range: { startDate: range.startDate, endDate: range.endDate },
    fixed,
    termijn: termijnVanDeal(deal),
  };
}

function isoDag(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * isDateAvailable van de datumstap: niet voorbij (met de boekingstermijn),
 * binnen de reisperiode; bij een vaste datum alleen de aankomstdag.
 */
export function isAankomstBoekbaar(date, aanvraag, { now = new Date() } = {}) {
  if (!aanvraag || !geldigeLinkDatum(date)) return false;
  const { startDate, endDate } = aanvraag.range;
  if (aanvraag.fixed) return date === startDate;
  const eerste = new Date(now.getFullYear(), now.getMonth(), now.getDate() + aanvraag.termijn);
  return date >= isoDag(eerste) && date >= startDate && date <= endDate;
}

/**
 * De voordeligste boekbare aankomstdagen (YYYY-MM-DD) uit een antwoord van
 * `/api/booking/calendar`: alleen dagen met een echte prijs voor de
 * standaardduur, klikbaar in de datumstap; laagste prijs eerst, bij gelijke
 * prijs de vroegste. Prijzen zijn als in priceMap afgerond op hele euro's.
 */
export function voordeligsteAankomstdagen(antwoord, aanvraag, { now = new Date(), max = MAX_AANKOMSTDAGEN } = {}) {
  if (!aanvraag || !antwoord || typeof antwoord !== "object" || antwoord.degraded) return [];
  const prices = antwoord.prices;
  if (!prices || typeof prices !== "object") return [];
  const kandidaten = [];
  for (const [date, perDuur] of Object.entries(prices)) {
    if (!perDuur || typeof perDuur !== "object") continue;
    const prijs = readPositiveCalendarPrice(perDuur[aanvraag.losKey]?.price);
    if (prijs === undefined) continue;
    const afgerond = Math.round(prijs);
    if (!isTegelKlikbaar(afgerond, isAankomstBoekbaar(date, aanvraag, { now }))) continue;
    kandidaten.push({ date, prijs: afgerond });
  }
  kandidaten.sort((a, b) => a.prijs - b.prijs || (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  return kandidaten.slice(0, max).map((k) => k.date);
}

/**
 * Het hotel (slug uit `hotels.hotel_id`) van de voordeligste getoonde dag: het
 * `cheapestHotelId` van de eerste dag uit voordeligsteAankomstdagen, of null
 * als de backend het niet meegeeft. `dagen` is de uitvoer van
 * voordeligsteAankomstdagen voor dit antwoord; de eerste datum telt.
 */
export function inbegrepenHotelId(antwoord, aanvraag, dagen) {
  if (!aanvraag || !antwoord || typeof antwoord !== "object" || !Array.isArray(dagen)) return null;
  const eerste = dagen[0];
  if (typeof eerste !== "string") return null;
  const perDuur = antwoord.prices?.[eerste];
  const id = perDuur && typeof perDuur === "object" ? perDuur[aanvraag.losKey]?.cheapestHotelId : undefined;
  return typeof id === "string" && id.trim() !== "" ? id : null;
}

/**
 * De boek-URL van de oranje knop met `date` en `los` (dagen) erbij, of null
 * als die URL al een ander gezelschap of een andere duur draagt: dan hoort de
 * kalender van 2 volwassenen er niet bij.
 */
export function aankomstHref(bookingHref, date, los) {
  if (typeof bookingHref !== "string" || !bookingHref.startsWith("/booking/")) return null;
  if (!geldigeLinkDatum(date) || geldigeLinkLos(los) == null) return null;
  const vraag = bookingHref.indexOf("?");
  const pad = vraag < 0 ? bookingHref : bookingHref.slice(0, vraag);
  const params = new URLSearchParams(vraag < 0 ? "" : bookingHref.slice(vraag + 1));
  if (params.has("adults") || params.has("children") || params.has("nights")) return null;
  params.delete("hotel");
  params.set(LINK_DATE_PARAM, date);
  params.set(LINK_LOS_PARAM, String(los));
  return `${pad}?${params.toString()}`;
}

/** Korte weekdag, dag en maand in de taal van de site, bijvoorbeeld "za 21 nov". */
export function aankomstLabel(date, locale) {
  const iso = geldigeLinkDatum(date);
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  return new Intl.DateTimeFormat(localeToBcp47(locale), {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(y, m - 1, d)));
}

/**
 * De hotels zoals het hotelblok (DealPartnerHotels) ze toont: alleen de hotels
 * van de deal (`hotelIds`), in de volgorde van de lijst (dealvolgorde, dan het
 * openingshotel vooraan, als in PartnerHotelsClient). Bij een kerstdeal komen
 * de hotels van «Jouw hotels» eerst, in de volgorde van dat blok.
 */
export function hotelsVanHotelblok(
  hotels,
  { hotelIds = [], leadHotelId = null, volgorde = null, kerstHotels = null, voorop = null } = {},
) {
  if (!Array.isArray(hotels)) return [];
  const ids = Array.isArray(hotelIds) ? hotelIds : [];
  const gefilterd = orderHotels(ids.length > 0 ? hotels.filter((h) => ids.includes(h?.hotel_id)) : hotels, volgorde);
  const lead = leadHotelId != null ? gefilterd.findIndex((h) => h?.hotel_id === leadHotelId) : -1;
  const lijst = lead > 0 ? [gefilterd[lead], ...gefilterd.filter((_, i) => i !== lead)] : gefilterd;
  let resultaat = lijst;
  if (Array.isArray(kerstHotels) && kerstHotels.length > 0) {
    const kerst = kerstHotelRijen(gefilterd, kerstHotels).map((r) => r.hotel);
    resultaat = [...kerst, ...lijst.filter((h) => !kerst.includes(h))];
  }
  return hotelVoorop(resultaat, voorop);
}

/** Zet het hotel met slug `voorop` als eerste; onbekend of leeg id: de lijst ongewijzigd. */
export function hotelVoorop(hotels, voorop) {
  if (!Array.isArray(hotels) || typeof voorop !== "string" || voorop === "") return hotels;
  const i = hotels.findIndex((h) => h?.hotel_id === voorop);
  if (i <= 0) return hotels;
  return [hotels[i], ...hotels.filter((_, j) => j !== i)];
}

/**
 * De hotelregel: één naam; twee of drie namen met komma's en «en»; meer dan
 * drie de eerste twee en «en nog N andere hotels». Null zonder namen.
 */
export function hotelRegel(hotels, t, locale) {
  if (!Array.isArray(hotels) || typeof t !== "function") return null;
  const namen = [];
  for (const h of hotels) {
    const naam = typeof h?.hotel_name === "string" ? h.hotel_name.trim() : "";
    if (naam && !namen.includes(naam)) namen.push(naam);
  }
  if (namen.length === 0) return null;
  if (namen.length === 1) return namen[0];
  if (namen.length <= 3) {
    return t("deal.boekregel.hotels_en", {
      lijst: namen.slice(0, -1).join(", "),
      laatste: namen[namen.length - 1],
    });
  }
  const aantal = namen.length - 2;
  const vars = { lijst: namen.slice(0, 2).join(", "), aantal };
  const vorm = new Intl.PluralRules(localeToBcp47(locale)).select(aantal);
  return t(`deal.boekregel.hotels_meer_${vorm}`, t("deal.boekregel.hotels_meer", vars), vars);
}
